import { getLanguage, http } from 'cdui-js';
import { aesEncryptHexByPass, encryptForgetLoginPasswordAt } from '../lib/crypto';
import { getEntryQueryParams } from '../lib/entryQuery';
import { objectToQueryString } from '../lib/utils';

export { encryptForgetLoginPasswordAt };

export const PASSWORD_ENCRYPT_TYPE_AES_USERNAME_TS = 'AES_USERNAME_TS' as const;

/** 忘记密码发码用途（UserCodeVerifyUseType.ForgetPassword） */
export const USE_TYPE_FORGET_PASSWORD = 111;

/** APP WebView：登录链路保持原行为，不注入 H5 deviceType / 不改登录路径 */
const isInAppWebView = () => Boolean((window as any).ReactNativeWebView);

/** 仅 H5：登录相关请求 body/query 注入 deviceType=H5 */
const withH5LoginDevice = <T extends Record<string, unknown>>(data: T): T => {
  if (isInAppWebView()) return data;
  return { ...data, deviceType: 'H5' };
};

export const getEncryPwd = (username, password) => {
  const timestamp = Date.now();
  const key = `${username}${timestamp}`;
  return {
    timestamp: `${timestamp}`,
    password: aesEncryptHexByPass(password, key),
  };
};

/** 手机号注册（验证码通过后设置密码触发） */
export const register = (data) => {
  const encry = getEncryPwd(data.username, data.password);
  data.timestamp = Number(encry.timestamp);
  data.password = encry.password;
  return http.post(`/user/register`, withH5LoginDevice(data)) as any;
};

// 登录
export const login = (data) => {
  // H5：落地页地址栏参数原样并入 body；调用方字段在后，覆盖同名项（username / password 等）
  const isApp = isInAppWebView();
  const payload = isApp ? data : { ...getEntryQueryParams(), ...data };
  const plainPassword = String(payload.password ?? '');
  // 新号/短信登录密码为空：保持空串，不 AES（避免把空密码加密成密文）
  if (plainPassword) {
    const encry = getEncryPwd(payload.username, plainPassword);
    payload.timestamp = encry.timestamp;
    payload.password = encry.password;
  } else {
    payload.password = '';
    payload.timestamp = String(Date.now());
  }
  payload.deviceCode = localStorage.getItem('DIFI_DEVICE_ID') || '';
  // 调用方可显式覆盖；未传值时沿用原默认行为 true，避免影响存量调用
  if (payload.autoSendVerifyCode === undefined) {
    payload.autoSendVerifyCode = true;
  }
  if (isApp) {
    // APP：保持原路径与 deviceType=WEB，不走 H5 专用登录
    payload.deviceType = 'WEB';
    return http.post(`/user/login`, payload) as any;
  }
  payload.deviceType = 'H5';
  return http.post(`/user/login/h5`, payload) as any;
};

/** 滑块拼图下发结构（与 App UserImageVerificationRespDTO 对齐） */
export type UserImageVerificationRespDTO = {
  type?: string;
  originImage?: string;
  shadeImage?: string;
  cutoutImage?: string;
  y?: number;
  x?: number;
  width?: number;
  height?: number;
  validateCode?: string;
};

/**
 * 发送验证码（短信/邮箱）。
 * 短信登录、二次验证、忘记密码等场景共用。
 *
 * @param data.sendCodeType '0' 短信、'1' 邮箱
 * @param data.account 接收账号（手机号/邮箱）
 * @param data.useType 业务用途，见 UserCodeVerifyUseType；登录用 102
 * @param data.loginChannel 可选；二次验证时回传 login 接口返回的 loginChannel
 * @param data.validateCode 强制滑块态必传：滑块会话码
 * @param data.slideX 强制滑块态必传：横向滑动距离（原图像素）
 * @returns 成功返回 verifyCodePrefix 字符串（如 "TTOJ"），提交时拼为 "TTOJ-123456"
 */
export const verifySendCode = (data: {
  sendCodeType: '0' | '1';
  account: string;
  contact?: string;
  countryCode?: string;
  useType: number;
  loginChannel?: string;
  validateCode?: string;
  slideX?: number;
}) => {
  return http.post(`/user/code/verifySend`, withH5LoginDevice(data)) as any;
};

/**
 * 发码前检查账号是否已注册，并返回是否强制滑块。
 *
 * @param data.account 手机号
 * @param data.sendCodeType '0' 短信、'1' 邮箱
 * @param data.countryCode 国际区号，如 +86；手机场景建议传入
 * @returns goBound=true 表示已开户；slideCaptchaRequired=true 时获取验证码须先完成滑块；
 *          强制时可附带 slideCaptcha 拼图数据
 */
export const userCheck = (data: {
  account: string;
  sendCodeType: '0' | '1';
  countryCode?: string;
}) => {
  return http.get(`/user/code/userCheck${objectToQueryString(withH5LoginDevice(data))}`) as any;
};

/**
 * 下发滑块拼图（无 body）。
 * 用于：userCheck 强制态但未带图时的兜底；弹层内「换一张」/失败自动刷新（不重新打 userCheck）。
 */
export const sendSliderCaptcha = () => {
  return http.post(
    `/user/login/sliderSend`,
    withH5LoginDevice({}),
  ) as Promise<UserImageVerificationRespDTO>;
};

/**
 * 校验验证码（用于短信登录前判断手机号是否已注册）。
 *
 * @returns { verifyCodeCorrect: boolean; phoneRegistered: boolean }
 */
export const verifyCheckCode = (data: {
  account: string;
  sendCodeType: '0' | '1';
  accountType: string;
  verifyCode: string;
  countryCode?: string;
}) => {
  return http.post(`/user/code/verifyCheck`, withH5LoginDevice(data)) as any;
};

// 游客登录
export const guestLogin = async ({ username, password, device }) => {
  const data = await login({
    username,
    password,
    validType: 1,
    userType: 'GUEST',
    device,
  });

  return data;
};

// 退出登录
export const logout = () => {
  return http.post(`/user/login/logout`) as any;
};

export const verifySmsCode = (data) => {
  const encry = getEncryPwd(data.username, data.password);
  data.timestamp = encry.timestamp;
  data.password = encry.password;
  data.deviceCode = localStorage.getItem('DIFI_DEVICE_ID') || '';
  if (isInAppWebView()) {
    data.deviceType = 'WEB';
  } else {
    data.deviceType = 'H5';
  }

  return http.post(`/user/login/loginQuadraticVerify`, data) as any;
};

//获取用户信息
export async function fetchUserInfo() {
  const lang = getLanguage();

  const res = (await http.get(`/user/info`, { headers: { locale: lang } })) as any;
  return res;
}

//更新用户头像、昵称、简介
export function updateIconNick(data) {
  return http.post(`/user/updateIconNick`, data) as any;
}

/** 忘记密码：账号是否已注册。data 为 boolean */
export const checkAccountExists = (data: {
  account: string;
  validType: '0' | '1';
  countryCode?: string;
}) => {
  return http.get(`/user/forgetPassword/accountExists${objectToQueryString(data)}`) as Promise<boolean>;
};

/**
 * 忘记登录密码。调用方须贴匿名 OPS 头；body.accountType 固定 "2"。
 * newPassword 须用 encryptForgetLoginPasswordAt 加密。
 */
export const forgetPassword = (
  data: {
    account: string;
    newPassword: string;
    validType: '0' | '1';
    verifyCode: string;
    accountType: string;
    encryptType: string;
    timestamp: number;
    countryCode?: string;
  },
  options?: { headers?: Record<string, string> },
) => {
  return http.post(`/user/forgetPassword`, data, options) as any;
};

/** OPS 设备钥曲线 */
export const OPS_DEVICE_KEY_ALG = 'ES256' as const;

/** 登记本机 OPS 公钥（可匿名，不传 userId；body.deviceCode 须与请求头一致） */
export const registerDevicePublicKey = (data: {
  deviceCode: string;
  devicePublicKey: string;
  keyId: string;
  deviceModel: string;
  opsPopSign: string;
  timestamp: number;
  keyAlg: string;
  signAlgo: string;
}) => {
  return http.post(`/user/device/registerPublicKey`, data) as any;
};
